/**
 * features/finance/periodEndWrite.ts
 * ------------------------------------------------------------------
 * Recording standing entries, and running the month.
 *
 * The maths lives in lib/recurring.ts and lib/yearEnd.ts, which are pure and
 * tested. This file is the part that touches Firestore: saving a template,
 * recording a deferral, and posting whatever the calendar says is owed.
 *
 * NOTHING HERE DECIDES WHAT IS OWED. It asks the pure functions, hands the
 * answer to postJournal, and reports what happened. Keeping the decision out
 * of the write path is what makes a month-end routine checkable — the tests
 * can drive every calendar edge without a database.
 *
 * THE SWEEP IS RESUMABLE AND SAFE TO REPEAT. Each generated journal carries
 * a source id built from its template and its month, so a run that fails
 * halfway leaves the journals it managed to post and the next run picks up
 * the rest. A run with nothing left to do posts nothing. This matters more
 * than it sounds: a bursar who is unsure whether the sweep completed will
 * press the button again, and that has to be the right thing to do.
 *
 * ONE FAILURE DOES NOT STOP THE MONTH. If one template is misconfigured the
 * rest still post, and the failure is returned rather than thrown. A month
 * that refuses to close because of one bad line is a month somebody
 * force-closes.
 * ------------------------------------------------------------------ */
import { collection, doc, getDocs, query, where } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { GL_COLLECTIONS, journalIdFor, type GlAccount, type GlJournal } from '@/lib/gl';
import {
  monthOf, recurringDue, recurringProblem, deferralDue, deferralProblem,
  deferralRecognitionLines, type RecurringTemplate, type Deferral, type DueEntry,
  type DeferralAccounts,
} from '@/lib/recurring';
import {
  buildYearEndPosition, yearEndClosingLines, yearEndTransferLines, yearEndProblem,
  yearEndSourceId, yearEndTransferSourceId, type YearEndAccounts,
} from '@/lib/yearEnd';
import { postJournal, closePeriod } from '@/features/finance/glWrite';
import { auditFromContext } from '@/lib/audit';

export interface Actor {
  uid?: string;
  name?: string;
  displayName?: string;
  email?: string;
}

const actorOf = (a: Actor) => ({ uid: a.uid, name: a.displayName ?? a.name, email: a.email });
const nowIso = () => new Date().toISOString();
const newId = (prefix: string) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

/* ========================== the accounts needed ========================= */

/**
 * Resolve the system accounts by KEY, never by hard-coded code.
 *
 * A school that renumbers its chart must not silently start posting
 * prepayments to whatever now sits at 1300. Missing keys are returned as a
 * list so the screen can name them rather than failing at posting time with
 * an account code nobody recognises.
 */
export function resolveKeys(
  accounts: GlAccount[],
  keys: string[],
): { found: Record<string, string>; missing: string[] } {
  const found: Record<string, string> = {};
  for (const a of accounts) if (a.systemKey && keys.includes(a.systemKey)) found[a.systemKey] = a.code;
  return { found, missing: keys.filter((k) => !found[k]) };
}

/* ============================== templates ============================== */

export async function saveRecurringTemplate(
  input: Omit<RecurringTemplate, 'id' | 'createdAt'> & { id?: string },
  actor: Actor,
): Promise<RecurringTemplate> {
  const problem = recurringProblem(input);
  if (problem) throw new Error(problem);

  const id = input.id ?? newId('rec');
  const record: RecurringTemplate = {
    ...input,
    id,
    createdAt: nowIso(),
    createdBy: actor.uid,
  };
  await setDoc(doc(db, FINANCE_COLLECTIONS.recurringTemplates, id), record);
  auditFromContext(actor as never, input.id ? 'recurring_template_updated' : 'recurring_template_created', 'finance', {
    targetId: id,
    targetType: 'journal',
    targetName: record.name,
    details: { startPeriod: record.startPeriod, endPeriod: record.endPeriod ?? null, lines: record.lines.length },
  });
  return record;
}

/**
 * Stop a template. Never delete one.
 *
 * What it posted stands, and a template that vanishes leaves twelve journals
 * whose narration refers to something no longer in the system.
 */
export async function stopRecurringTemplate(
  id: string,
  reason: string,
  actor: Actor,
): Promise<void> {
  if (!reason.trim()) throw new Error('Say why it is stopping — the journals it posted refer to it.');
  await updateDoc(doc(db, FINANCE_COLLECTIONS.recurringTemplates, id), {
    active: false,
    stoppedAt: nowIso(),
    stoppedBy: actor.uid,
    stoppedReason: reason.trim(),
  });
  auditFromContext(actor as never, 'recurring_template_stopped', 'finance', {
    targetId: id,
    targetType: 'journal',
    details: { reason: reason.trim() },
  });
}

/* ============================== deferrals ============================== */

/**
 * Record a deferral AND post the entry that sets it up, in that order.
 *
 * The recognition entry is posted here rather than left to the sweep because
 * it is not periodic: it happens once, when the school decides the cost
 * belongs somewhere other than where it landed. Leaving it to a monthly job
 * would mean the accounts were briefly wrong in a way nobody had asked for.
 */
export async function createDeferral(
  input: Omit<Deferral, 'id' | 'createdAt' | 'active'>,
  accounts: DeferralAccounts,
  actor: Actor,
): Promise<Deferral> {
  const problem = deferralProblem(input);
  if (problem) throw new Error(problem);

  const id = newId('def');
  const record: Deferral = { ...input, id, active: true, createdAt: nowIso(), createdBy: actor.uid };

  await setDoc(doc(db, FINANCE_COLLECTIONS.deferrals, id), record);
  await postJournal({
    type: 'deferral',
    sourceId: `${id}-open`,
    date: record.recognisedOn,
    narration: `${record.description} — ${record.kind === 'prepayment' ? 'carried forward' : 'accrued'}`,
    lines: deferralRecognitionLines(record, accounts),
    actor: actorOf(actor),
  });

  auditFromContext(actor as never, 'deferral_created', 'finance', {
    targetId: id,
    targetType: 'journal',
    targetName: record.description,
    details: { kind: record.kind, amount: record.amount, months: record.months, startPeriod: record.startPeriod },
  });
  return record;
}

/* ============================== the sweep ============================== */

export interface SweepPlan {
  upTo: string;
  recurring: { template: RecurringTemplate; entries: DueEntry[] }[];
  deferrals: { deferral: Deferral; entries: DueEntry[] }[];
  totalEntries: number;
}

export interface SweepResult {
  posted: number;
  skipped: number;
  failures: { sourceId: string; narration: string; error: string }[];
}

/**
 * Every source id already in the ledger for these two types.
 *
 * Exported because the screens need it too: "how much of this prepayment is
 * still to be charged" is only answerable against what has actually posted,
 * and answering it against an empty set gives a figure that reads true and
 * is not.
 */
export async function postedSourceIds(): Promise<Set<string>> {
  const out = new Set<string>();
  for (const type of ['recurring', 'deferral'] as const) {
    const snap = await getDocs(query(
      collection(db, GL_COLLECTIONS.journals),
      where('source.type', '==', type),
    ));
    for (const d of snap.docs) {
      const src = (d.data() as GlJournal).source?.id;
      if (src) out.add(src);
    }
  }
  return out;
}

/**
 * What the month owes, without posting any of it.
 *
 * Read first, post second, always. A bursar seeing forty journals about to
 * appear has a chance to notice that one of them is wrong; a bursar seeing
 * "done" does not.
 */
export async function planPeriodEnd(
  templates: RecurringTemplate[],
  deferrals: Deferral[],
  upTo: string,
  accounts: DeferralAccounts,
): Promise<SweepPlan> {
  const posted = await postedSourceIds();

  const rec = templates
    .map((template) => ({ template, entries: recurringDue(template, upTo, posted) }))
    .filter((r) => r.entries.length > 0);

  const def = deferrals
    .filter((d) => d.active)
    .map((deferral) => ({ deferral, entries: deferralDue(deferral, upTo, posted, accounts) }))
    .filter((r) => r.entries.length > 0);

  return {
    upTo,
    recurring: rec,
    deferrals: def,
    totalEntries: rec.reduce((a, r) => a + r.entries.length, 0)
      + def.reduce((a, r) => a + r.entries.length, 0),
  };
}

/**
 * Post the plan.
 *
 * Failures are COLLECTED, not thrown. One misconfigured template must not
 * stop the other thirty from posting — a month that refuses to close because
 * of one bad line is a month somebody eventually force-closes.
 */
export async function runPeriodEnd(plan: SweepPlan, actor: Actor): Promise<SweepResult> {
  const result: SweepResult = { posted: 0, skipped: 0, failures: [] };

  const all: { type: 'recurring' | 'deferral'; entry: DueEntry }[] = [
    ...plan.recurring.flatMap((r) => r.entries.map((entry) => ({ type: 'recurring' as const, entry }))),
    ...plan.deferrals.flatMap((r) => r.entries.map((entry) => ({ type: 'deferral' as const, entry }))),
  ];

  for (const { type, entry } of all) {
    try {
      await postJournal({
        type,
        sourceId: entry.sourceId,
        date: entry.date,
        narration: entry.narration,
        lines: entry.lines,
        actor: actorOf(actor),
      });
      result.posted += 1;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // A journal already there is the sweep having run before, not a fault.
      if (/already exists/i.test(message)) result.skipped += 1;
      else result.failures.push({ sourceId: entry.sourceId, narration: entry.narration, error: message });
    }
  }

  auditFromContext(actor as never, 'period_end_run', 'finance', {
    targetId: `period-end-${plan.upTo}`,
    targetType: 'journal',
    details: {
      upTo: plan.upTo,
      posted: result.posted,
      alreadyThere: result.skipped,
      failed: result.failures.length,
    },
  });
  return result;
}

/* ============================== year end =============================== */

export interface YearEndResult {
  closingJournal: string;
  transferJournal?: string;
  surplus: number;
  periodsLocked: number;
}

/**
 * Close the year: empty the income statement, move the result to the fund,
 * then lock every month in it.
 *
 * THE LOCK IS PART OF CLOSING, not a separate tidy-up. A closed year whose
 * months still accept postings is not closed: the next journal dated into it
 * makes the accumulated fund wrong with nothing to indicate it, because the
 * closing entry has already been taken.
 *
 * The two journals are posted in order and the second is only attempted if
 * the first succeeded. Posting the transfer without the close would credit
 * the fund with a surplus the income accounts still hold.
 */
export async function closeYear(
  journals: GlJournal[],
  accounts: GlAccount[],
  fy: string,
  post: YearEndAccounts,
  actor: Actor,
): Promise<YearEndResult> {
  const position = buildYearEndPosition(journals, accounts, fy);
  const problem = yearEndProblem(position);
  if (problem) throw new Error(problem);

  const closingLines = yearEndClosingLines(position, post);
  const lastDay = `${fy.replace(/^FY/, '')}-12-31`;

  const closing = await postJournal({
    type: 'year_end',
    sourceId: yearEndSourceId(fy),
    date: lastDay,
    narration: `Closing entry — ${fy}`,
    lines: closingLines,
    actor: actorOf(actor),
  });

  let transfer: GlJournal | undefined;
  const transferLines = yearEndTransferLines(position, post);
  if (transferLines.length) {
    transfer = await postJournal({
      type: 'year_end',
      sourceId: yearEndTransferSourceId(fy),
      date: lastDay,
      narration: position.surplus >= 0
        ? `Surplus for ${fy} to accumulated fund`
        : `Deficit for ${fy} to accumulated fund`,
      lines: transferLines,
      actor: actorOf(actor),
    });
  }

  // Lock the months. A month already closed is left as it is.
  const months = new Set(
    journals.filter((j) => j.fy === fy).map((j) => monthOf(j.date)).filter(Boolean),
  );
  let periodsLocked = 0;
  for (const period of months) {
    try {
      await closePeriod(period, `${fy} closed`, actorOf(actor));
      periodsLocked += 1;
    } catch {
      // Already closed. Closing a year twice must not fail on the lock.
    }
  }

  auditFromContext(actor as never, 'year_end_closed', 'finance', {
    targetId: yearEndSourceId(fy),
    targetType: 'journal',
    targetName: fy,
    details: {
      surplus: position.surplus,
      income: position.totalIncome,
      expense: position.totalExpense,
      accountsClosed: position.rows.length,
      periodsLocked,
    },
  });

  return {
    closingJournal: closing.journalNo,
    transferJournal: transfer?.journalNo,
    surplus: position.surplus,
    periodsLocked,
  };
}

/** Has this year already been closed? Read straight off the ledger. */
export function isYearClosed(journals: GlJournal[], fy: string): boolean {
  return journals.some(
    (j) => j.fy === fy && j.source?.type === 'year_end' && j.id === journalIdFor('year_end', yearEndSourceId(fy)),
  );
}
